import React, { ChangeEvent, useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { excludeRuChars, translate } from '@utils';
import { createRole, makeRoles, setCurrentRolePermissions, updateRole } from '@actions/roleActions';
import { Paths } from '@constants/paths';
import { pageNavigationRoutes } from '@constants/navigation';
import { useNavigate, useParams } from 'react-router-dom';
import { currentUserRoleSelector } from '@selectors/user';
import {
  currentRolePermissionsSelector,
  isRolePermissionsSaving,
  roleByIdSelector,
} from '@selectors/role';
import { getAllSubmissionGroupListSelector } from '@selectors/submissionGroups';
import { makeAllSubmissionGroups } from '@actions/submissionGroupsActions';

export const useChangeRole = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { id } = useParams();

  const allGroups = useSelector(getAllSubmissionGroupListSelector);
  const role = useSelector(roleByIdSelector(id));
  const newRole = useSelector(currentUserRoleSelector);
  const currentRole = useSelector(id ? roleByIdSelector(id) : currentUserRoleSelector);
  const currentRolePermissions = useSelector(currentRolePermissionsSelector);
  const isSaving = useSelector(isRolePermissionsSaving);
  const [editValue, setEditValue] = useState('');
  const [errorText, setErrorText] = useState('');

  useEffect(() => {
    if (!role || !newRole) {
      dispatch(makeRoles());
    }
    return () => {
      dispatch(setCurrentRolePermissions(null));
    };
  }, []);

  useEffect(() => {
    if (currentRole && id) {
      setEditValue(currentRole.name);
      dispatch(setCurrentRolePermissions(currentRole.pagePermissions));
    }
  }, [id, currentRole]);

  useEffect(() => {
    if (!id && newRole) {
      dispatch(setCurrentRolePermissions(newRole.pagePermissions));
    }
  }, [newRole]);

  useEffect(() => {
    if (!allGroups?.length) {
      dispatch(makeAllSubmissionGroups);
    }
  }, []);

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = excludeRuChars(event.target.value).trimStart();
      setEditValue(value);

      if (value.length < 3) {
        setErrorText(translate('min_3_chars'));
      } else if (value.length >= 20) {
        setErrorText(translate('max_20_chars'));
      } else {
        setErrorText('');
      }
    },
    [editValue]
  );

  const handleCreateRole = async () => {
    const result = await dispatch(
      createRole({ name: editValue, pagePermissions: currentRolePermissions })
    );
    if (result) {
      navigate(`${Paths.DASHBOARD}${pageNavigationRoutes.ROLES}`);
    }
  };

  const handleEditRole = async () => {
    const result = await dispatch(
      updateRole({ id, name: editValue, pagePermissions: currentRolePermissions })
    );
    if (result) {
      navigate(`${Paths.DASHBOARD}${pageNavigationRoutes.ROLES}`);
    }
  };

  const handleCancel = () => {
    navigate(`${Paths.DASHBOARD}${pageNavigationRoutes.ROLES}`);
  };

  const disablePage = () => {
    return {
      opacity: isSaving ? 0.5 : 1,
      pointerEvents: isSaving
        ? ('none' as React.CSSProperties['pointerEvents'])
        : ('auto' as React.CSSProperties['pointerEvents']),
    };
  };

  return {
    editValue,
    errorText,
    handleInputChange,
    handleCreateRole,
    handleEditRole,
    handleCancel,
    currentRolePermissions,
    isSaving,
    disablePage,
  };
};
